import React from 'react';
import { MARK_LINK } from 'storyblok-rich-text-react-renderer';
import { ErrorMessage, Field } from 'formik';
import type { StoryblokRichtext } from '../../../.storyblok/types/storyblok';
import { renderRichTextReact } from '../../utils/storyblokRichText';

/** @type {RenderOptions} */
const renderOptions = {
  markResolvers: {
    [MARK_LINK]: (children: React.ReactNode, { href }: { href?: string }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-cerulean-400 hover:text-cerulean-700 hover:italic"
      >
        {children}
      </a>
    ),
  },
};

interface StandaloneEmailFormProps {
  onFormSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting?: boolean;
  label?: string;
  submitLabel?: string;
  disclaimerText?: StoryblokRichtext;
}

/**
 * The large, standalone presentation of the newsletter signup form used on the /newsletter
 * landing page: a labelled underlined input above an orange SUBMIT pill. The header and
 * footer presentations remain the compact placeholder-plus-enter-arrow variants.
 */
function StandaloneEmailForm({
  onFormSubmit,
  isSubmitting = false,
  label,
  submitLabel,
  disclaimerText,
}: StandaloneEmailFormProps) {
  // Storyblok sends cleared text fields as '', which a default parameter wouldn't catch.
  const labelText = label?.trim() || 'EMAIL';
  const submitText = submitLabel?.trim() || 'SUBMIT';

  return (
    <form onSubmit={onFormSubmit} noValidate>
      {/* The label doubles as the placeholder, matching the header/footer newsletter forms:
          it clears as soon as the field is typed into rather than sitting above the rule. */}
      <Field
        id="newsletter-email"
        name="email"
        type="email"
        placeholder={labelText}
        aria-label={labelText}
        className="typography-eyebrow-m !text-navy-600 placeholder:text-navy-600 h-[34px] w-full border-0 border-b
          border-solid border-b-gray-100 bg-transparent focus:outline-none"
      />

      <div className="typography-body-xs !text-orange-700 mt-[6px] empty:hidden">
        <ErrorMessage name="email" />
      </div>

      {disclaimerText && (
        <div className="typography-body-xs !text-gray-300 mt-[10px]">
          {renderRichTextReact(disclaimerText, renderOptions)}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-[30px] h-[35px] w-[138px] rounded-[20px] border-[0.75px] border-solid border-orange-400
          bg-orange-400 font-brown text-[12px] font-bold uppercase leading-[1.7] tracking-[2.16px] text-beige-100
          hover:bg-orange-700 disabled:opacity-35 md:h-[40px] md:text-[16px] md:leading-[1.6] md:tracking-[2.88px]"
      >
        {submitText}
      </button>
    </form>
  );
}

export default StandaloneEmailForm;
